import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Map, 
  Compass, 
  TrendingUp, 
  Briefcase, 
  Landmark, 
  Calculator, 
  Banknote, 
  Users, 
  Activity, 
  FileText,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';
import styles from './HubNav.module.css';

const hubData = [
  {
    id: 'planning',
    label: 'Planning',
    icon: Map,
    links: [
      { title: 'Complete Retirement Planning Guide', path: '/how-to-plan-retirement' },
      { title: 'How to Plan for Retirement', path: '/how-to-plan-retirement' },
      { title: 'How Much Do You Need to Retire?', path: '/how-much-to-retire' },
      { title: 'How to Retire Early (FIRE)', path: '/retire-early' },
      { title: 'How Much You Need to Earn', path: '/average-salary-by-state' },
      { title: 'Retirement Statistics 2026', path: '/retirement-statistics-2026' },
      { title: 'Retirement Savings Gap', path: '/retirement-savings-gap-gender-race' },
      { title: 'Roth vs. Traditional', path: '/roth-vs-traditional' },
      { title: 'Best Planning Tools', path: '/plootus-vs-best-retirement-tools' },
    ]
  },
  {
    id: 'life-stages',
    label: 'By Life Stage',
    icon: Compass,
    links: [
      { title: 'Retirement Planning in Your 30s', path: '/retirement-planning-in-your-30s' },
      { title: 'Retirement Planning in Your 40s', path: '/retirement-planning-in-your-40s' },
      { title: 'Retirement Planning in Your 50s', path: '/retirement-planning-in-your-50s' },
    ]
  },
  {
    id: 'benchmarks',
    label: 'Benchmarks',
    icon: TrendingUp,
    links: [
      { title: 'Average 401(k) Balance by Age', path: '/401k-by-age' },
      { title: '401(k) Savings Guide by Age', path: '/401k-by-age' },
      { title: 'Average 403(b) Balance by Age', path: '/average-403b-balance-by-age' },
      { title: 'Average IRA Balance by Age', path: '/average-ira-balance-by-age' },
      { title: 'Average Net Worth by Age', path: '/average-net-worth-by-age' },
      { title: 'Average Savings by Age', path: '/average-savings-by-age' },
      { title: 'Average Credit Card Debt', path: '/average-credit-card-debt' },
      { title: 'Average Student Loan Debt', path: '/average-student-loan-debt' },
    ]
  },
  {
    id: 'accounts',
    label: 'Accounts',
    icon: Briefcase,
    links: [
      { title: '403(b) Plan Guide', path: '/403b-guide' },
      { title: '457(b) Plan Guide', path: '/457b-plan-guide' },
      { title: 'TSP (Thrift Savings Plan) Guide', path: '/tsp-thrift-savings-plan-guide' },
      { title: 'HSA Contribution Limits', path: '/hsa-contribution-limits' },
      { title: 'Social Security Benefits Guide', path: '/social-security-benefits' },
      { title: 'Roth IRA vs. Traditional IRA', path: '/roth-vs-traditional' },
      { title: 'Backdoor Roth IRA Guide', path: '/backdoor-roth-ira' },
      { title: 'Switching Jobs 401(k) Guide', path: '/switching-jobs-401k' },
    ]
  },
  {
    id: 'state-hub',
    label: 'By State',
    icon: Landmark,
    links: [
      { title: 'Retire by State Rankings', path: '/best-states-to-retire' },
      { title: 'Best States to Retire (2026)', path: '/best-states-to-retire' },
      { title: 'Cheapest States to Retire', path: '/cheapest-states-to-retire' },
      { title: 'Best States for Early Retirement', path: '/best-states-for-early-retirement' },
      { title: 'Tax-Friendly States for Retirees', path: '/tax-friendly-states-for-retirees' },
      { title: 'Most Tax-Friendly States', path: '/tax-friendly-states-for-retirees' },
      { title: 'State Retirement Data Comparison', path: '/tool-state-comparison' },
      { title: 'State Retirement Comparison Tool', path: '/tool-state-comparison' },
      { title: 'Average Monthly Expenses by State', path: '/average-monthly-expenses-by-state' },
    ]
  },
  {
    id: 'calculators',
    label: 'Calculators',
    icon: Calculator,
    links: [
      { title: 'Retirement Income Calculator', path: '/retirement-income-calculator' },
      { title: 'Compound Interest Calculator', path: '/compound-interest-calculator' },
      { title: 'Inflation Impact Calculator', path: '/inflation-retirement-calculator' },
      { title: 'Social Security Calculator', path: '/social-security-calculator' },
      { title: 'RMD Calculator', path: '/rmd-calculator' },
      { title: 'State Comparison Tool', path: '/tool-state-comparison' },
      { title: 'Medicare Supplement Comparison', path: '/medicare-supplement-medigap-comparison' },
      { title: 'Best Retirement Tools Comparison', path: '/plootus-vs-best-retirement-tools' },
    ]
  },
  {
    id: 'income-costs',
    label: 'Income & Costs',
    icon: Banknote,
    links: [
      { title: 'Average Salary by State', path: '/average-salary-by-state' },
      { title: 'Median Household Income', path: '/median-household-income' },
      { title: 'Minimum Wage by State', path: '/minimum-wage-by-state' },
      { title: 'Average Rent by City', path: '/rent-by-city' },
      { title: 'Average Monthly Expenses by State', path: '/average-monthly-expenses-by-state' },
      { title: 'Average Net Worth by Age', path: '/average-net-worth-by-age' },
    ]
  },
  {
    id: 'family-debt',
    label: 'Family & Debt',
    icon: Users,
    links: [
      { title: 'Cost of Raising a Child', path: '/cost-of-raising-child-by-state' },
      { title: 'Childcare Costs by State', path: '/childcare-costs' },
      { title: 'Average Student Loan Debt', path: '/average-student-loan-debt' },
      { title: 'Average Credit Card Debt', path: '/average-credit-card-debt' },
      { title: 'Retirement Savings Gap', path: '/retirement-savings-gap-gender-race' },
      { title: 'Average Savings by Age', path: '/average-savings-by-age' },
      { title: 'Divorce & Retirement', path: '/divorce-and-retirement' },
      { title: 'Estate Planning Basics', path: '/estate-planning-basics' },
    ]
  },
  {
    id: 'health',
    label: 'Healthcare',
    icon: Activity,
    links: [
      { title: 'Medicare Guide 2026', path: '/medicare-guide' },
      { title: 'Healthcare Costs in Retirement', path: '/healthcare-costs-in-retirement' },
      { title: 'Medigap Plan Comparison', path: '/medicare-supplement-medigap-comparison' },
      { title: 'Health Insurance Costs by State', path: '/health-insurance-costs' },
      { title: 'Long-Term Care Costs by State', path: '/long-term-care-costs' },
      { title: 'HSA Contribution Limits & Strategy', path: '/hsa-contribution-limits' },
      { title: 'Social Security & Medicare Timing', path: '/social-security-benefits' },
    ]
  },
  {
    id: 'taxes',
    label: 'Taxes',
    icon: FileText,
    links: [
      { title: 'Retirement Tax Guide 2026', path: '/retirement-tax-guide' },
      { title: 'Federal Income Tax Brackets', path: '/federal-income-tax-brackets' },
      { title: 'Capital Gains Tax Rates', path: '/capital-gains-tax-rates' },
      { title: 'Roth Conversion Strategy', path: '/roth-conversion-strategy' },
      { title: 'Social Security Tax Calculator', path: '/social-security-tax-calculator' },
      { title: 'Standard Deduction 2026', path: '/standard-deduction-2026' },
      { title: 'Tax-Loss Harvesting Guide', path: '/tax-loss-harvesting' },
      { title: 'IRA Contribution Limits', path: '/ira-contribution-limits' },
    ]
  }
];

const HubNav = ({ activeTab, setActiveTab }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const navRef = React.useRef(null);

  const isHubPage = location.pathname === '/retirement-guide';

  const handleSectionClick = (id) => {
    if (isHubPage) {
      const el = document.getElementById(id);
      if (el) {
        const offset = 140;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = el.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
      if (setActiveTab) setActiveTab(id);
    } else {
      navigate(`/retirement-guide#${id}`);
    }
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleLinkClick = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  // Scroll to hash anchor after cross-page navigation
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      // Small delay to let the page render its sections first
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const offset = 140;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = el.getBoundingClientRect().top;
          const offsetPosition = elementRect - bodyRect - offset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 991) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate dropdown position to prevent overflow
  useEffect(() => {
    if (openDropdown && navRef.current) {
      const navItem = navRef.current.querySelector(`[data-section="${openDropdown}"]`);
      if (navItem) {
        const rect = navItem.getBoundingClientRect();
        const dropdownWidth = 280;
        const viewportWidth = window.innerWidth;
        const rightEdge = rect.right + dropdownWidth;
        
        if (rightEdge > viewportWidth) {
          // Shift dropdown to the left
          setDropdownPosition({
            left: 'auto',
            right: 0
          });
        } else {
          setDropdownPosition({
            left: 0,
            right: 'auto'
          });
        }
      }
    }
  }, [openDropdown]);

  return (
    <nav className={styles.hubNav} aria-label="Global Financial Resource Navigation" ref={navRef}>
      <div className={styles.navContainer}>
        {/* Toggle and Logo for Mobile */}
        <div className={styles.navHeader}>
          <div className={styles.hubBrand} onClick={() => navigate('/retirement-guide')}>
            <span className={styles.brandText}>Plootus Resource Hub</span>
          </div>
          <button 
            className={styles.mobileMenuToggle}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Navigation"
          >
            {isMobileMenuOpen ? <ArrowRight size={24} color="#1a1a1a" /> : <ArrowLeft size={24} color="#1a1a1a" />}
          </button>
        </div>

        {/* Navigation Items */}
        <div className={`${styles.navContent} ${isMobileMenuOpen ? styles.show : ''}`}>
          <div className={styles.navList}>
            {hubData.map((section) => (
              <div 
                key={section.id} 
                data-section={section.id}
                className={`${styles.navItem} ${activeTab === section.id ? styles.active : ''}`}
                onMouseEnter={() => setOpenDropdown(section.id)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <div 
                  className={styles.sectionTrigger}
                  onClick={() => handleSectionClick(section.id)}
                >
                  <span className={styles.sectionIcon}>
                    <section.icon size={16} strokeWidth={2.5} />
                  </span>
                  <span className={styles.sectionLabel}>{section.label}</span>
                </div>

                {/* Dropdown - Desktop standard list */}
                <div 
                  className={`${styles.dropdown} ${openDropdown === section.id ? styles.open : ''}`}
                  style={openDropdown === section.id ? dropdownPosition : {}}
                >
                  <ul className={styles.dropdownList}>
                    {section.links.map((link, idx) => (
                      <li key={idx} className={styles.dropdownItem}>
                        <Link 
                          to={link.path}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setOpenDropdown(null);
                          }}
                        >
                          {link.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default HubNav;
